import { useState } from "react";
import { Gift, Lock, Check, Sparkles, ChevronRight, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { GiftTier, GiftItem, formatPrice } from "@/data/giftData";
import { VariantPickerDialog } from "./VariantPickerDialog";

interface GiftRewardsProps {
  subtotal: number;
  tiers: GiftTier[];
  /** tierId -> giftId */
  selectedGifts: Record<string, string>;
  /** tierId -> zvolený variant */
  selectedVariants?: Record<string, string>;
  onSelectGift: (tierId: string, giftId: string, variant?: string) => void;
  variant?: "compact" | "full";
  currency?: string;
  /** Koľko darčekov v méte zobraziť (zvyšok cez odkaz na /darceky). 0 = všetky */
  maxPerTier?: number;
  /** Zobraziť odkaz na prehľad všetkých darčekov */
  showAllLink?: boolean;
}

export const GiftRewards = ({
  subtotal,
  tiers,
  selectedGifts,
  selectedVariants = {},
  onSelectGift,
  variant = "full",
  currency = "€",
  maxPerTier = 0,
  showAllLink = false,
}: GiftRewardsProps) => {
  const [variantGift, setVariantGift] = useState<{ tierId: string; gift: GiftItem } | null>(null);

  if (tiers.length === 0) return null;

  const compact = variant === "compact";
  const sorted = [...tiers].sort((a, b) => a.threshold - b.threshold);
  const maxThreshold = sorted[sorted.length - 1].threshold;
  const progress = Math.min((subtotal / maxThreshold) * 100, 100);

  const unlocked = sorted.filter((t) => subtotal >= t.threshold);
  const nextTier = sorted.find((t) => subtotal < t.threshold);
  const pendingChoice = unlocked.filter((t) => !selectedGifts[t.id]).length;

  return (
    <div
      className={cn(
        "rounded-2xl border bg-gradient-to-br from-primary/5 via-background to-accent/30",
        unlocked.length > 0 ? "border-primary/30" : "border-border",
        compact ? "p-3.5" : "p-5"
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className={cn(
            "rounded-xl flex items-center justify-center flex-shrink-0 transition-colors",
            compact ? "w-9 h-9" : "w-10 h-10",
            unlocked.length > 0
              ? "bg-primary text-primary-foreground"
              : "bg-primary/10 text-primary"
          )}
        >
          <Gift className={compact ? "w-4.5 h-4.5" : "w-5 h-5"} />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={cn(
              "font-semibold text-foreground leading-tight",
              compact ? "text-sm" : "text-base"
            )}
          >
            Darčeky k objednávke
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {unlocked.length === 0
              ? `Prvý darček získate od ${formatPrice(sorted[0].threshold, currency)}`
              : pendingChoice > 0
              ? `Vyberte si ${pendingChoice === 1 ? "darček" : `${pendingChoice} darčeky`} — sú na nás`
              : "Darčeky sú vybrané, pribalíme ich zadarmo"}
          </p>
        </div>
        {unlocked.length > 0 && (
          <span className="flex-shrink-0 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
            {unlocked.length}/{sorted.length}
          </span>
        )}
      </div>

      {/* Milestone progress */}
      <div className={cn("relative", compact ? "mb-3 pb-5" : "mb-4 pb-6")}>
        <div className="progress-bar-container">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary/70 to-primary transition-all duration-700 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        {sorted.map((tier) => {
          const left = Math.min((tier.threshold / maxThreshold) * 100, 100);
          const reached = subtotal >= tier.threshold;
          return (
            <div
              key={tier.id}
              className="absolute top-0 -translate-x-1/2 flex flex-col items-center"
              style={{ left: `${left}%` }}
            >
              <span
                className={cn(
                  "w-4 h-4 -mt-1 rounded-full border-2 flex items-center justify-center transition-colors",
                  reached
                    ? "bg-primary border-primary text-primary-foreground"
                    : "bg-background border-border text-muted-foreground"
                )}
              >
                {reached ? (
                  <Check className="w-2.5 h-2.5" strokeWidth={3} />
                ) : (
                  <Lock className="w-2 h-2" />
                )}
              </span>
              <span
                className={cn(
                  "mt-1.5 text-[10px] font-semibold whitespace-nowrap",
                  reached ? "text-primary" : "text-muted-foreground"
                )}
              >
                {tier.threshold} {currency}
              </span>
            </div>
          );
        })}
      </div>

      {/* Next tier hint */}
      {nextTier && (
        <p
          className={cn(
            "flex items-start gap-1.5 text-foreground mb-3",
            compact ? "text-xs" : "text-sm"
          )}
        >
          <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
          <span>
            Nakúpte ešte za{" "}
            <span className="font-bold text-primary">
              {formatPrice(nextTier.threshold - subtotal, currency)}
            </span>{" "}
            a vyberiete si darček z{" "}
            <span className="font-medium">
              {nextTier.gifts.length} možností
            </span>
            .
          </span>
        </p>
      )}

      {/* Tiers */}
      <div className="space-y-3">
        {sorted.map((tier) => {
          const reached = subtotal >= tier.threshold;
          const selected = selectedGifts[tier.id];

          if (!reached) {
            return (
              <div
                key={tier.id}
                className={cn(
                  "flex items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/30",
                  compact ? "p-2.5" : "p-3"
                )}
              >
                <div className="flex -space-x-2 flex-shrink-0">
                  {tier.gifts.slice(0, 3).map((g) => (
                    <img
                      key={g.id}
                      src={g.image}
                      alt={g.name}
                      className="w-8 h-8 rounded-lg object-cover border-2 border-background grayscale opacity-70"
                    />
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    {tier.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {tier.gifts.length} darčekov na výber
                  </p>
                </div>
                <span className="text-[11px] font-semibold text-primary whitespace-nowrap">
                  ešte {formatPrice(tier.threshold - subtotal, currency)}
                </span>
              </div>
            );
          }

          return (
            <div
              key={tier.id}
              className={cn(
                "rounded-xl border transition-all duration-300",
                compact ? "p-2.5" : "p-3",
                reached
                  ? selected
                    ? "border-primary/40 bg-primary/5"
                    : "border-primary bg-primary/[0.07] shadow-sm shadow-primary/10"
                  : "border-border bg-secondary/40"
              )}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <p
                  className={cn(
                    "font-semibold flex items-center gap-1.5",
                    compact ? "text-xs" : "text-sm",
                    reached ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {reached ? (
                    <Gift className="w-3.5 h-3.5 text-primary" />
                  ) : (
                    <Lock className="w-3.5 h-3.5" />
                  )}
                  {reached ? "Vyberte si 1 darček zadarmo" : tier.label}
                </p>
                {reached ? (
                  selected ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-primary">
                      Vybrané
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-primary animate-pulse">
                      Vyberte
                    </span>
                  )
                ) : (
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                    ešte {formatPrice(tier.threshold - subtotal, currency)}
                  </span>
                )}
              </div>

              <div
                className={cn(
                  "flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-0.5 px-0.5 pb-1",
                  !reached && "opacity-60"
                )}
              >
                {(maxPerTier > 0 ? tier.gifts.slice(0, maxPerTier) : tier.gifts).map((gift) => {
                  const isSelected = selected === gift.id;
                  return (
                    <button
                      key={gift.id}
                      type="button"
                      disabled={!reached}
                      onClick={() => {
                        if (gift.variants?.length && selected !== gift.id) {
                          setVariantGift({ tierId: tier.id, gift });
                          return;
                        }
                        onSelectGift(tier.id, gift.id, selectedVariants[tier.id]);
                      }}
                      className={cn(
                        "group relative flex-shrink-0 snap-start rounded-xl border bg-background text-left transition-all duration-200",
                        compact ? "w-[104px] p-1.5" : "w-[132px] p-2",
                        reached
                          ? "hover:border-primary/60 hover:shadow-md hover:shadow-primary/10 active:scale-[0.97] cursor-pointer"
                          : "cursor-not-allowed",
                        isSelected
                          ? "border-primary ring-2 ring-primary/25"
                          : "border-border"
                      )}
                    >
                      <div className="relative mb-1.5">
                        <img
                          src={gift.image}
                          alt={gift.name}
                          loading="lazy"
                          className={cn(
                            "w-full aspect-square object-cover rounded-lg bg-secondary transition-all",
                            !reached && "grayscale"
                          )}
                        />
                        {isSelected && (
                          <span className="absolute inset-0 rounded-lg bg-primary/15 flex items-center justify-center">
                            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                              <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            </span>
                          </span>
                        )}
                        {!isSelected && reached && (
                          <span className="discount-badge absolute top-1 left-1">
                            Zadarmo
                          </span>
                        )}
                        {gift.variants?.length && !isSelected && (
                          <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-full bg-background/90 border border-border text-[9px] font-semibold text-foreground flex items-center gap-0.5">
                            <SlidersHorizontal className="w-2.5 h-2.5" />
                            {gift.variants.length}
                          </span>
                        )}
                      </div>
                      <p
                        className={cn(
                          "font-medium text-foreground line-clamp-2 leading-tight mb-0.5",
                          compact ? "text-[11px] min-h-[26px]" : "text-xs min-h-[30px]"
                        )}
                      >
                        {gift.name}
                      </p>
                      {isSelected && selectedVariants[tier.id] ? (
                        <p className="text-[10px] font-semibold text-primary truncate">
                          {selectedVariants[tier.id]}
                        </p>
                      ) : (
                        <p className="text-[10px] text-muted-foreground">
                          <span className="line-through">
                            {formatPrice(gift.value, currency)}
                          </span>{" "}
                          <span className="text-primary font-bold">0 {currency}</span>
                        </p>
                      )}
                    </button>
                  );
                })}

                {maxPerTier > 0 && tier.gifts.length > maxPerTier && (
                  <Link
                    to="/darceky"
                    className={cn(
                      "flex-shrink-0 snap-start rounded-xl border border-dashed border-primary/40 bg-primary/5 text-primary flex flex-col items-center justify-center gap-1 text-center transition-colors hover:bg-primary/10",
                      compact ? "w-[104px] p-1.5" : "w-[132px] p-2"
                    )}
                  >
                    <span className="text-lg font-bold leading-none">
                      +{tier.gifts.length - maxPerTier}
                    </span>
                    <span className="text-[10px] font-semibold leading-tight">
                      ďalších darčekov
                    </span>
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAllLink && (
        <Link
          to="/darceky"
          className={cn(
            "mt-3 flex items-center justify-center gap-1.5 w-full rounded-xl border border-primary/40 bg-primary/5 font-semibold text-primary transition-all hover:bg-primary/10 active:scale-[0.99]",
            compact ? "h-9 text-xs" : "h-11 text-sm"
          )}
        >
          Prehľad všetkých darčekov a ponúk
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}

      {variantGift && (
        <VariantPickerDialog
          open
          onOpenChange={(o) => !o && setVariantGift(null)}
          name={variantGift.gift.name}
          image={variantGift.gift.image}
          variants={variantGift.gift.variants ?? []}
          variantLabel={variantGift.gift.variantLabel ?? "Variant"}
          currentVariant={selectedVariants[variantGift.tierId]}
          confirmLabel="Pridať darček zadarmo"
          priceNode={
            <p className="text-xs text-muted-foreground">
              <span className="line-through">{formatPrice(variantGift.gift.value, currency)}</span>{" "}
              <span className="text-primary font-bold">0 {currency}</span>
            </p>
          }
          onConfirm={(v) => {
            onSelectGift(variantGift.tierId, variantGift.gift.id, v);
            setVariantGift(null);
          }}
        />
      )}
    </div>
  );
};
